import { useEffect, useState, type ReactNode } from 'react';
import { DateField as DatePicker, toDayInput } from '../date-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

/** One label/value line of the panel. The grid keeps every value on one axis. */
export function FieldRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[8.5rem_minmax(0,1fr)] items-start gap-x-3 py-0.5">
      <span className="pt-1.5 text-sm text-muted-foreground">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/** Radix cannot hold an empty value, so "nothing chosen" needs a real token. */
const NONE = '__none__';

export interface SelectFieldProps {
  label: string;
  value: string | null;
  options: readonly { value: string; label: string }[];
  onChange: (value: string | null) => void | Promise<void>;
  placeholder?: string;
  emptyLabel?: string;
  disabled?: boolean;
}

/**
 * An enum field that commits on pick and shows the new value at once, rolling
 * back only if the save rejects.
 */
export function SelectField({
  label,
  value,
  options,
  onChange,
  placeholder = 'Sin definir',
  emptyLabel = 'Sin definir',
  disabled = false,
}: SelectFieldProps) {
  const [optimistic, setOptimistic] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setOptimistic(null);
  }, [value]);

  const shown = optimistic ?? value;

  const commit = async (next: string) => {
    const resolved = next === NONE ? null : next;
    setOptimistic(resolved);
    setError(null);
    try {
      await onChange(resolved);
    } catch (caught) {
      setOptimistic(null);
      setError(caught instanceof Error ? caught.message : 'No pudimos guardar el cambio');
    }
  };

  return (
    <div className="space-y-1">
      <Select value={shown ?? NONE} onValueChange={(next: string) => void commit(next)} disabled={disabled}>
        <SelectTrigger aria-label={label} className="border-transparent bg-transparent shadow-none hover:bg-muted">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>{emptyLabel}</SelectItem>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

export interface ToggleFieldProps {
  label: string;
  value: boolean;
  onChange: (value: boolean) => void | Promise<void>;
}

export function ToggleField({ label, value, onChange }: ToggleFieldProps) {
  const [optimistic, setOptimistic] = useState<boolean | null>(null);

  useEffect(() => {
    setOptimistic(null);
  }, [value]);

  const shown = optimistic ?? value;

  return (
    <div className="flex h-8 items-center gap-2">
      <Switch
        checked={shown}
        aria-label={label}
        onCheckedChange={(next: boolean) => {
          setOptimistic(next);
          void Promise.resolve(onChange(next)).catch(() => setOptimistic(null));
        }}
      />
      <span className={cn('text-sm', !shown && 'text-muted-foreground')}>{shown ? 'Sí' : 'No'}</span>
    </div>
  );
}

export interface DateFieldProps {
  label: string;
  /** ISO instant, or null when the field is empty. */
  value: string | null;
  onChange: (value: string | null) => void | Promise<void>;
}

/**
 * A date field that shows the pick at once and rolls it back only if the save
 * rejects — the same bargain the select above it makes.
 */
export function DateField({ label, value, onChange }: DateFieldProps) {
  const [optimistic, setOptimistic] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Anything that changes the record elsewhere wins over the local pick.
  useEffect(() => {
    setOptimistic(null);
  }, [value]);

  const commit = async (day: string) => {
    setOptimistic(day);
    setError(null);
    // Midday UTC: a date-only field must not slide a day either side of Chile.
    const next = day ? new Date(`${day}T12:00:00.000Z`).toISOString() : null;
    try {
      await onChange(next);
    } catch (caught) {
      setOptimistic(null);
      setError(caught instanceof Error ? caught.message : 'No pudimos guardar el cambio');
    }
  };

  return (
    <div className="space-y-1">
      <DatePicker
        clearable
        value={optimistic ?? toDayInput(value)}
        aria-label={label}
        placeholder="Sin definir"
        className="border-transparent bg-transparent shadow-none hover:bg-muted"
        onChange={(day) => void commit(day)}
      />
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
